/**
 * Cursor rendering utilities for preview and export.
 * Renders animated cursor overlay with click effects on video frames.
 */

import { type CursorSettings } from '@/store/editor-store'
import { type MouseEvent as TrackedMouseEvent } from '@/store/recording-store'

// SVG cursor image cache
const SVG_CURSOR_CACHE = new Map<string, HTMLImageElement | ImageBitmap>()
const SVG_CURSOR_PATHS: Record<string, string> = {
    'default': '/normalcursor.svg',
    'pointer': '/HandCursor.svg',
    'modern-arrow': '/Cursor1.svg',
    'classic-arrow': '/normalcursor.svg',
    'hand-detailed': '/HandCursor.svg',
    'hand-minimal': '/HandCursor2.svg',
}

// Hotspot offsets for each SVG cursor (normalized 0-1 within the SVG viewbox)
// The hotspot is where the "click point" of the cursor is
const SVG_CURSOR_HOTSPOTS: Record<string, { x: number; y: number }> = {
    'default': { x: 0.32, y: 0.18 },           // tip of the classic arrow
    'pointer': { x: 0.35, y: 0.14 },           // fingertip
    'modern-arrow': { x: 0.15, y: 0.1 },      // tip of the arrow
    'classic-arrow': { x: 0.32, y: 0.18 },     // tip of the classic arrow
    'hand-detailed': { x: 0.35, y: 0.14 },     // fingertip
    'hand-minimal': { x: 0.47, y: 0.27 },      // fingertip area
}

/** Preload all SVG cursor images. Call once at app startup. */
export function preloadCursorSvgs(): void {
    for (const [key, path] of Object.entries(SVG_CURSOR_PATHS)) {
        if (SVG_CURSOR_CACHE.has(key)) continue
        const img = new Image()
        img.src = path
        img.onload = () => SVG_CURSOR_CACHE.set(key, img)
        img.onerror = () => console.warn(`[CursorRenderer] Failed to load cursor SVG: ${path}`)
    }
}

// Auto-preload on module import
if (typeof window !== 'undefined') {
    preloadCursorSvgs()
}

/**
 * Draw an SVG-based cursor with color tinting.
 * Uses a temp canvas to apply the tint color via compositing.
 */
function drawSvgCursor(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    style: string,
    scale: number,
    color: string,
): void {
    const img = SVG_CURSOR_CACHE.get(style)
    if (!img) return // Not loaded yet, skip this frame

    const hotspot = SVG_CURSOR_HOTSPOTS[style] || { x: 0, y: 0 }
    
    // Target size: ~28px at scale 1.0 (matching the default arrow cursor size)
    const targetSize = 28 * scale
    const aspect = (img as HTMLImageElement).naturalWidth / (img as HTMLImageElement).naturalHeight || 1
    const drawW = targetSize * (aspect >= 1 ? 1 : aspect)
    const drawH = targetSize * (aspect >= 1 ? 1 / aspect : 1)

    // Offset so the hotspot is at (0,0) — the caller already translated to cursor position
    const offsetX = -hotspot.x * drawW
    const offsetY = -hotspot.y * drawH

    // For tinting, we use a temporary canvas approach:
    // 1. Draw the SVG image
    // 2. Use 'source-in' composite to tint it
    // But only if the color isn't white (which means "use original colors")
    const isWhite = color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#fff'
    const needsTint = !isWhite && style !== 'classic-arrow' // classic-arrow looks best with original b/w colors

    if (needsTint) {
        // Create a temp canvas for tinting
        const tempCanvas = document.createElement('canvas')
        const tW = Math.ceil(drawW + 4)
        const tH = Math.ceil(drawH + 4)
        tempCanvas.width = tW
        tempCanvas.height = tH
        const tCtx = tempCanvas.getContext('2d')!

        // Draw the original SVG
        tCtx.drawImage(img, 2, 2, drawW, drawH)

        // Apply tint: fill with color using source-atop to only affect non-transparent pixels
        tCtx.globalCompositeOperation = 'source-atop'
        tCtx.fillStyle = color
        tCtx.fillRect(0, 0, tW, tH)
        tCtx.globalCompositeOperation = 'source-over'

        // Draw tinted result to main canvas
        ctx.drawImage(tempCanvas, offsetX - 2, offsetY - 2)
    } else {
        // Draw without tinting
        ctx.drawImage(img, offsetX, offsetY, drawW, drawH)
    }
}

/**
 * Interpolated cursor position with click proximity info.
 */
export interface CursorPosition {
    x: number   // 0-1 normalized
    y: number   // 0-1 normalized
    type: 'move' | 'click' | 'dwell' | 'shape'
    /** Active cursor shape at this moment (from shape events in the metadata stream) */
    shape: 'arrow' | 'hand' | 'ibeam' | 'wait' | 'crosshair' | 'resize'
    /** Time since the last click (ms). Used for click effect animation. */
    timeSinceLastClick: number
    /** Position of last click in normalized coords */
    lastClickX: number
    lastClickY: number
}

/**
 * Find the interpolated cursor position at a given time.
 * Uses linear interpolation between tracked events with optional smoothing.
 */
export function interpolateCursorPosition(
    mouseData: TrackedMouseEvent[],
    timeMs: number,
    screenWidth: number,
    screenHeight: number,
    smoothing: number = 0.7
): CursorPosition | null {
    if (!mouseData || mouseData.length === 0) return null

    // Find the surrounding events
    let before: TrackedMouseEvent | null = null
    let after: TrackedMouseEvent | null = null
    let lastClick: TrackedMouseEvent | null = null

    let activeShape: CursorPosition['shape'] = 'arrow'

    for (let i = 0; i < mouseData.length; i++) {
        const event = mouseData[i]

        if (event.type === 'click' && event.t <= timeMs) {
            lastClick = event
        }

        // Track the most recent shape event at or before timeMs
        if (event.type === 'shape' && event.shape && event.t <= timeMs) {
            activeShape = event.shape as CursorPosition['shape']
        }

        if (event.t <= timeMs) {
            before = event
        } else {
            after = event
            break
        }
    }

    if (!before) {
        // Before any events — use the first event position
        const first = mouseData[0]
        return {
            x: first.x / screenWidth,
            y: first.y / screenHeight,
            type: first.type,
            shape: activeShape,
            timeSinceLastClick: Infinity,
            lastClickX: 0,
            lastClickY: 0,
        }
    }

    // Normalize coordinates
    let x = before.x / screenWidth
    let y = before.y / screenHeight

    // Interpolate between before and after
    if (after && after.t !== before.t) {
        const t = (timeMs - before.t) / (after.t - before.t)
        // Apply smoothing via ease function
        const easedT = smoothing > 0
            ? smoothStep(t, smoothing)
            : t
        x = before.x / screenWidth + (after.x / screenWidth - before.x / screenWidth) * easedT
        y = before.y / screenHeight + (after.y / screenHeight - before.y / screenHeight) * easedT
    }

    // Clamp to 0-1
    x = Math.max(0, Math.min(1, x))
    y = Math.max(0, Math.min(1, y))

    return {
        x,
        y,
        type: before.type,
        shape: activeShape,
        timeSinceLastClick: lastClick ? timeMs - lastClick.t : Infinity,
        lastClickX: lastClick ? lastClick.x / screenWidth : 0,
        lastClickY: lastClick ? lastClick.y / screenHeight : 0,
    }
}

/**
 * Smooth step interpolation for cursor path smoothing.
 */
function smoothStep(t: number, strength: number): number {
    // Blend between linear (strength=0) and cubic smoothstep (strength=1)
    const smooth = t * t * (3 - 2 * t)
    return t + (smooth - t) * strength
}

// ─── macOS-style cursor SVG path data ───────────────────────────

const CURSOR_PATHS = {
    default: {
        // Classic macOS pointer arrow
        outline: 'M 0,0 L 0,17.2 L 4.4,13.2 L 7.8,20 L 10.8,18.6 L 7.2,12 L 12.6,12 Z',
        fill: 'M 1,1.8 L 1,14.8 L 4.2,12 L 7.8,19 L 9.6,18.2 L 6,11.2 L 11,11.2 Z',
    },
    pointer: {
        // Hand pointer (User provided SVG paths)
        // outline is the details (fingernails, etc.), fill is the hand shape
        outline: 'M35.9732 13.8979C35.2389 13.9098 34.4704 13.9988 33.6728 14.1718C28.1242 15.376 25.2203 21.2523 27.0078 27.369C27.8717 30.3246 31.4132 42.0283 31.4132 42.0283C31.4132 42.0283 28.2741 39.2207 24.0157 39.2207C20.0143 39.2207 15.6797 42.9202 15.6797 48.1535C15.6797 54.1563 19.4651 55.4596 24.9082 64.9721C28.9207 71.9846 34.8664 74.7601 36.1387 77.5073C37.2678 79.9462 36.1395 81.9574 37.4923 84.1588C38.309 85.4894 40.4525 86.1065 41.0025 86.1065H51.8749C54.886 86.1065 57.9567 82.7184 57.9567 82.7184C57.9567 82.7184 60.4058 86.1065 64.8794 86.1065H71.5201C73.9631 86.1065 74.39 84.9685 74.9136 84.1615C75.4372 83.3532 75.2608 80.9757 75.2608 78.5951C75.2608 69.6174 84.313 69.2864 84.313 52.1086C84.313 50.7739 84.3212 44.5769 84.3212 44.1686C84.3212 39.2283 83.29 36.6914 79.4872 35.0567C76.0511 33.5803 72.9225 35.6508 72.9225 35.6508C72.9225 35.6508 71.2173 32.1174 66.602 31.2563C61.7062 30.3438 58.7244 32.9625 58.7244 32.9625C58.7244 32.9625 57.3791 31.0994 54.0152 30.3855C50.9138 29.7272 48.5193 30.9443 48.5193 30.9443C48.5193 30.9443 47.2807 25.405 46.2515 22.2827C44.6729 17.4921 41.1132 13.8147 35.9732 13.8979ZM36.1468 22.1634C37.2832 22.1627 37.8978 23.5788 38.1895 24.4854C38.9089 26.7229 43.9913 42.4212 44.8274 44.7545C45.5774 46.8462 46.4423 45.4889 46.6367 44.5375C47.2881 41.35 49.0638 38.3814 52.336 38.5995C56.4277 38.8731 56.3455 43.3878 56.6275 46.0865C56.7789 47.5253 58.1759 47.3857 58.5453 46.119C58.9384 44.7718 60.1027 39.4008 64.2013 39.7605C68.5999 40.1466 67.441 45.3256 67.4077 46.7104C67.3757 48.0479 68.668 48.0465 69.4639 46.7104C70.3222 45.2701 71.5787 44.2911 73.4189 44.7925C75.1537 45.2661 75.9567 46.6793 75.9553 48.7584C75.9553 49.4029 75.9499 52.1059 75.9499 52.1059C75.9499 59.4087 74.0641 62.0599 71.8836 65.1321C70.1238 67.6085 67.562 71.4405 67.112 75.6058C67.0495 76.1863 66.8629 76.8574 66.4935 77.1574C66.124 77.4574 63.8595 77.7406 63.8595 77.7406C63.8595 77.7406 60.796 76.4906 60.1377 75.717C59.1321 74.5364 57.4675 74.5299 56.5244 75.3508C56.0258 75.7841 54.0618 77.7406 52.8243 77.7406H45.8772C44.9772 77.7406 44.7406 77.1463 44.6239 76.7505C44.2197 75.3852 43.4215 73.4013 42.6979 72.4943C40.8243 70.1457 40.3014 69.3739 38.2139 67.5573C36.068 65.6892 33.7313 63.8204 32.1646 60.8163C28.8632 54.4857 24.0401 51.212 24.0401 48.6689C24.0401 47.8981 25.3117 47.6028 25.6867 47.6028C26.4687 47.6028 27.8923 47.3727 33.2117 50.3616C37.6089 52.8325 41.4201 48.5852 40.4437 44.9227C39.6298 41.8672 35.9315 28.1104 35.0482 25.2396C34.6968 24.0993 34.5079 22.6417 35.626 22.2556C35.8102 22.1921 35.9845 22.1635 36.1468 22.1634ZM49.599 56.9399C48.8323 56.9399 48.2101 57.5621 48.2101 58.3287V69.4399C48.2101 70.2065 48.8323 70.8287 49.599 70.8287C50.3656 70.8287 50.9878 70.2065 50.9878 69.4399V58.3287C50.9878 57.5621 50.3656 56.9399 49.599 56.9399ZM56.5434 56.9399C55.7767 56.9399 55.1545 57.5621 55.1545 58.3287V69.4399C55.1545 70.2065 55.7767 70.8287 56.5434 70.8287C57.3101 70.8287 57.9323 70.2065 57.9323 69.4399V58.3287C57.9323 57.5621 57.3101 56.9399 56.5434 56.9399ZM63.4878 56.9399C62.7212 56.9399 62.099 57.5621 62.099 58.3287V69.4399C62.099 70.2065 62.7212 70.8287 63.4878 70.8287C64.2545 70.8287 64.8767 70.2065 64.8767 69.4399V58.3287C64.8767 57.5621 64.2545 56.9399 63.4878 56.9399Z',
        fill: 'M27.0752 59.2291L46.2705 84.1236L57.1189 79.2556L67.4105 82.4543L76.0332 66.8779L80.4836 51.9968L78.8147 41.9834L69.2185 37.8111L56.1454 36.1422L45.5757 35.3078L41.6815 19.5923L34.0324 19.3145L29.0257 25.4339L34.4496 37.811L35.2841 46.5728L24.7144 43.3749L19.8438 49.7722L27.0752 59.2291Z',
    },
}

/**
 * Draw a cursor on a 2D canvas context.
 * @param skipCursorShape - When true, only draws effects (spotlight, click effects) but not the
 *   cursor shape itself. Use for extension recordings where the cursor is already baked into the video.
 */
export function drawCursorOnCanvas(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    position: CursorPosition,
    settings: CursorSettings,
    canvasWidth: number,
    canvasHeight: number,
    skipCursorShape = false,
): void {
    const pixelX = position.x * canvasWidth
    const pixelY = position.y * canvasHeight

    // Spotlight overlay — drawn first, behind everything
    if (settings.spotlight) {
        const radius = (settings.spotlightRadius ?? 0.22) * Math.min(canvasWidth, canvasHeight)
        const opacity = settings.spotlightOpacity ?? 0.65
        const gradient = ctx.createRadialGradient(pixelX, pixelY, 0, pixelX, pixelY, radius)
        gradient.addColorStop(0,    'rgba(0,0,0,0)')
        gradient.addColorStop(0.55, 'rgba(0,0,0,0)')
        gradient.addColorStop(1,    `rgba(0,0,0,${opacity})`)
        ctx.save()
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
        ctx.restore()
    }

    // Draw click effect (behind cursor)
    if (settings.highlightClicks && settings.clickEffect !== 'none' && position.timeSinceLastClick < 500) {
        drawClickEffect(
            ctx,
            position.lastClickX * canvasWidth,
            position.lastClickY * canvasHeight,
            position.timeSinceLastClick,
            settings,
        )
    }

    // Draw cursor shape (skipped for extension recordings where cursor is baked into video)
    if (skipCursorShape) return
    ctx.save()
    ctx.translate(pixelX, pixelY)

    const baseScale = (canvasWidth / 1920) * settings.size  // Scale relative to 1080p
    const scale = Math.max(0.5, baseScale)

    if (settings.style === 'circle') {
        // Filled circle with subtle border — clean & modern (most popular for screen recorders)
        const radius = 10 * scale
        ctx.beginPath()
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
        ctx.fillStyle = hexWithAlpha(settings.color, 0.85)
        ctx.fill()
        ctx.strokeStyle = 'rgba(0,0,0,0.35)'
        ctx.lineWidth = 2 * scale
        ctx.stroke()
    } else if (settings.style === 'ring') {
        // Hollow ring — minimal, great for light-colored content
        const radius = 10 * scale
        ctx.beginPath()
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
        ctx.strokeStyle = settings.color
        ctx.lineWidth = 3 * scale
        ctx.stroke()
        // Shadow for visibility on any background
        ctx.beginPath()
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(0,0,0,0.4)'
        ctx.lineWidth = 5 * scale
        ctx.globalCompositeOperation = 'destination-over'
        ctx.stroke()
        ctx.globalCompositeOperation = 'source-over'
        // Center dot
        ctx.beginPath()
        ctx.arc(0, 0, 2.5 * scale, 0, Math.PI * 2)
        ctx.fillStyle = settings.color
        ctx.fill()
    } else if (settings.style === 'dot') {
        // Small dot cursor
        const radius = 6 * scale
        ctx.beginPath()
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
        ctx.fillStyle = settings.color
        ctx.fill()
        ctx.strokeStyle = 'rgba(0,0,0,0.4)'
        ctx.lineWidth = 1.5 * scale
        ctx.stroke()
    } else if (settings.style === 'crosshair') {
        // Crosshair cursor
        const size = 10 * scale
        ctx.strokeStyle = settings.color
        ctx.lineWidth = 2 * scale
        ctx.beginPath()
        ctx.moveTo(-size, 0)
        ctx.lineTo(size, 0)
        ctx.moveTo(0, -size)
        ctx.lineTo(0, size)
        ctx.stroke()
        // Center dot
        ctx.beginPath()
        ctx.arc(0, 0, 2 * scale, 0, Math.PI * 2)
        ctx.fillStyle = settings.color
        ctx.fill()
    } else {
        // 'auto' maps the shape recorded in metadata to the matching cursor style
        const resolvedStyle = settings.style === 'auto'
            ? (position.shape === 'hand' ? 'pointer' : 'default')
            : settings.style
        
        // Check if this is an SVG-based custom cursor
        if (resolvedStyle in SVG_CURSOR_PATHS) {
            drawSvgCursor(ctx, resolvedStyle, scale, settings.color)
        } else {
            // Built-in Path2D cursors
            const pathType = resolvedStyle === 'pointer' ? 'pointer' : 'default'
            const paths = CURSOR_PATHS[pathType] || CURSOR_PATHS.default

            ctx.save()
            if (pathType === 'pointer') {
                ctx.scale(scale * 0.45, scale * 0.45)
                ctx.translate(-35, -14)
            } else {
                ctx.scale(scale, scale)
            }

            if (pathType === 'pointer') {
                const fill = new Path2D(paths.fill)
                ctx.fillStyle = settings.color
                ctx.fill(fill)
                const outline = new Path2D(paths.outline)
                ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
                ctx.fill(outline)
            } else {
                const outline = new Path2D(paths.outline)
                ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
                ctx.fill(outline)
                const fill = new Path2D(paths.fill)
                ctx.fillStyle = settings.color
                ctx.fill(fill)
            }
            ctx.restore()
        }
    }

    ctx.restore()
}

/**
 * Draw click effect animation.
 */
function drawClickEffect(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    x: number,
    y: number,
    timeSinceClick: number,
    settings: CursorSettings,
): void {
    const duration = 500 // ms
    const progress = Math.min(1, timeSinceClick / duration)
    const opacity = 1 - easeOutCubic(progress)
    const maxRadius = settings.clickEffectSize * 25

    ctx.save()

    if (settings.clickEffect === 'ripple') {
        // Expanding ring that fades out
        const radius = maxRadius * easeOutCubic(progress)
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.strokeStyle = hexWithAlpha(settings.clickEffectColor, opacity * 0.8)
        ctx.lineWidth = 2.5 * (1 - progress)
        ctx.stroke()

        // Second, smaller ring
        if (progress < 0.8) {
            const innerProgress = Math.min(1, progress * 1.5)
            const innerRadius = maxRadius * 0.6 * easeOutCubic(innerProgress)
            ctx.beginPath()
            ctx.arc(x, y, innerRadius, 0, Math.PI * 2)
            ctx.strokeStyle = hexWithAlpha(settings.clickEffectColor, opacity * 0.4)
            ctx.lineWidth = 1.5 * (1 - innerProgress)
            ctx.stroke()
        }
    } else if (settings.clickEffect === 'pulse') {
        // Filled circle that expands and fades
        const radius = maxRadius * easeOutCubic(progress)
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.fillStyle = hexWithAlpha(settings.clickEffectColor, opacity * 0.3)
        ctx.fill()
    } else if (settings.clickEffect === 'ring') {
        // Solid expanding ring
        const innerRadius = maxRadius * 0.7 * easeOutCubic(progress)
        const outerRadius = maxRadius * easeOutCubic(progress)
        ctx.beginPath()
        ctx.arc(x, y, outerRadius, 0, Math.PI * 2)
        ctx.arc(x, y, innerRadius, 0, Math.PI * 2, true)
        ctx.fillStyle = hexWithAlpha(settings.clickEffectColor, opacity * 0.5)
        ctx.fill()
    }

    ctx.restore()
}

/**
 * Convert hex color + alpha to rgba string
 */
function hexWithAlpha(hex: string, alpha: number): string {
    // Parse hex
    const r = parseInt(hex.slice(1, 3), 16) || 0
    const g = parseInt(hex.slice(3, 5), 16) || 0
    const b = parseInt(hex.slice(5, 7), 16) || 0
    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`
}

function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3)
}
